import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import classNames from "classnames";
import styles from "./Modal.module.scss";

type ModalProps = {
  open: boolean;
  title?: string;
  description?: string;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  onClose: () => void;
  size?: "sm" | "md" | "lg";
  className?: string;
};

function Modal({
  open,
  title,
  description,
  children,
  footer,
  onClose,
  size = "md",
  className,
}: ModalProps) {
  useEffect(() => {
    if (!open || typeof document === "undefined") {
      return;
    }
    const body = document.body;
    const html = document.documentElement;
    const originalOverflow = body.style.overflow;
    const originalPaddingRight = body.style.paddingRight;
    const originalHtmlOverflow = html.style.overflow;
    const originalHtmlPaddingRight = html.style.paddingRight;
    const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
    body.style.overflow = "hidden";
    html.style.overflow = "hidden";
    if (scrollBarWidth > 0) {
      body.style.paddingRight = `${scrollBarWidth}px`;
      html.style.paddingRight = `${scrollBarWidth}px`;
    }
    return () => {
      body.style.overflow = originalOverflow;
      body.style.paddingRight = originalPaddingRight;
      html.style.overflow = originalHtmlOverflow;
      html.style.paddingRight = originalHtmlPaddingRight;
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  const content = (
    <div className={styles.overlay} role="presentation" onClick={onClose}>
      <div
        className={classNames(styles.modal, styles[size], className)}
        role="dialog"
        aria-modal="true"
        onClick={(event) => event.stopPropagation()}
      >
        <button type="button" className={styles.closeButton} aria-label="Close" onClick={onClose}>
          &times;
        </button>
        {(title || description) && (
          <header className={styles.header}>
            {title && <h2>{title}</h2>}
            {description && <p>{description}</p>}
          </header>
        )}
        <div className={styles.body}>{children}</div>
        {footer && <footer className={styles.footer}>{footer}</footer>}
      </div>
    </div>
  );

  const mountNode = typeof document !== "undefined" ? document.body : null;
  return mountNode ? createPortal(content, mountNode) : content;
}

export default Modal;
